"use client"

import { ComponentPropsWithoutRef, ReactNode, useState } from "react"
import {
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "@ui/shad"
import { buttonVariants } from "@ui/shad/button"
import { cn } from "@ui/utils"
import type { VariantProps } from "class-variance-authority"
import { ChevronsUpDown, Loader2 } from "lucide-react"

import { useDebounce } from "@/hooks/use-debounce"

interface Option {
  value: string
  label: string | ReactNode
  textLabel?: string
}

type ButtonSize = VariantProps<typeof buttonVariants>["size"] // 'default' | 'sm' | 'xs' | etc.

interface MultiSelectInputProps {
  id: string
  className?: string
  values?: string[]
  onChange?: (values: string[]) => void
  options: Option[]
  onSearch?: (query: string) => void
  isSearching?: boolean
  label?: ReactNode
  placeholder?: string
  emptyText?: string
  disabled?: boolean
  error?: boolean
  triggerButton?: ReactNode
  maxDisplayValues?: number
  contentProps?: ComponentPropsWithoutRef<typeof PopoverContent>
  includeSelectAllOption?: boolean
  selectAllOptionLabel?: string
  helperText?: string
  footer?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  size?: ButtonSize
}

const SEARCH_DEBOUNCE_MS = 300

function displayTextFor(option: Option): string {
  if (option.textLabel) return option.textLabel
  if (typeof option.label === "string") return option.label
  return option.value
}

export function MultiSelectInput({
  id,
  className,
  values = [],
  onChange,
  options = [],
  onSearch,
  isSearching = false,
  label,
  placeholder = "Select options",
  emptyText = "No results found.",
  disabled = false,
  error,
  triggerButton,
  maxDisplayValues = 3,
  contentProps,
  includeSelectAllOption = false,
  selectAllOptionLabel = "Select all",
  helperText,
  footer,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  size = "default",
}: MultiSelectInputProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [isTyping, setIsTyping] = useState(false)

  const open = controlledOpen ?? internalOpen
  const setOpen = controlledOnOpenChange ?? setInternalOpen

  const debouncedSearch = useDebounce((value: string) => {
    if (!onSearch) return
    if (value.trim().length > 0) {
      onSearch(value)
    } else {
      setIsTyping(false)
      onSearch("")
    }
  }, SEARCH_DEBOUNCE_MS)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (onSearch) {
      if (value.trim()) {
        setIsTyping(true)
        debouncedSearch(value)
      } else {
        setIsTyping(false)
        onSearch("")
      }
    }
  }

  const selectedOptions = options.filter((option) => values.includes(option.value))
  const filteredOptions = onSearch
    ? options
    : options.filter((option) => {
        const searchText = option.textLabel ?? (typeof option.label === "string" ? option.label : null)
        if (searchText) {
          return searchText.toLowerCase().includes(searchQuery.toLowerCase())
        }
        return true
      })
  const allSelected = values.length === options.length

  const handleSelect = (value: string) => {
    const newValues = values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
    onChange?.(newValues)
  }

  const handleSelectAll = () => {
    if (allSelected) {
      onChange?.([])
    } else {
      onChange?.(options.map((option) => option.value))
    }
  }

  return (
    <div className={cn("space-y-1", className)}>
      {label && (
        <Label htmlFor={id} className={cn(error && "text-destructive")}>
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {triggerButton ? (
            triggerButton
          ) : (
            <Button
              id={id}
              variant="outline"
              size={size}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              disabled={disabled}
              className={cn("w-full justify-between bg-transparent")}
              onClick={() => setOpen(!open)}
            >
              <div className="flex min-w-0 items-center font-normal">
                {selectedOptions.length === 0 ? (
                  <span className="text-muted-foreground truncate">{placeholder}</span>
                ) : (
                  <span className="truncate">
                    {selectedOptions.length <= maxDisplayValues
                      ? selectedOptions.map((option) => displayTextFor(option)).join(", ")
                      : `${selectedOptions
                          .slice(0, Math.max(1, maxDisplayValues - 2))
                          .map((option) => displayTextFor(option))
                          .join(", ")} and ${selectedOptions.length - Math.max(1, maxDisplayValues - 2)} more`}
                  </span>
                )}
              </div>
              <ChevronsUpDown
                className={cn(
                  "ml-2 shrink-0 opacity-50",
                  size === "xs" ? "size-3" : size === "sm" ? "size-3.5" : "size-4"
                )}
              />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" {...contentProps}>
          <Command filter={onSearch ? () => 1 : undefined}>
            <CommandInput
              placeholder={isSearching || isTyping ? "Searching..." : placeholder}
              className="h-8"
              value={searchQuery}
              onValueChange={handleSearchChange}
              icon={isSearching || isTyping ? <Loader2 className="mr-2.5 h-4 w-4 animate-spin" /> : undefined}
            />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              <ScrollArea>
                <CommandGroup className="max-h-[200px] pt-0.5">
                  {includeSelectAllOption && options.length > 0 && (
                    <CommandItem
                      value={selectAllOptionLabel}
                      onSelect={handleSelectAll}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label={selectAllOptionLabel}
                        className="mr-1"
                        tabIndex={-1}
                      />
                      <span>{selectAllOptionLabel}</span>
                    </CommandItem>
                  )}
                  {filteredOptions.map((option) => {
                    const isSelected = values.includes(option.value)
                    const labelText =
                      option.textLabel ?? (typeof option.label === "string" ? option.label : option.value)
                    return (
                      <CommandItem
                        key={option.value}
                        value={labelText}
                        data-value={option.value}
                        onSelect={() => handleSelect(option.value)}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelect(option.value)}
                          aria-label={`Select ${labelText}`}
                          className="mr-1"
                          tabIndex={-1}
                        />
                        <span className="min-w-0 flex-1">{option.label}</span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
            {footer}
          </Command>
        </PopoverContent>
      </Popover>
      {helperText && <p className="text-muted-foreground text-xs">{helperText}</p>}
    </div>
  )
}
